import { useMemo, useState, type FormEvent } from 'react'
import {
  useServices, useCreateService, useUpdateService, useDeleteService,
  useServiceInfo, useUpdateServiceInfo, useEventTimetable
} from '../hooks/useServices'
import { parseIsoDate } from '../lib/dates'
import {
  ModalShell, Field, inputClass, CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useArchived } from '../contexts/ArchiveContext'
import { SERVICE_TYPES, PULLMAN_MODES } from '../types'
import type { Service, ServiceCreate, ServiceType, PullmanMode, EventServiceInfo, TimetableRow } from '../types'

// ServiceTimetableTab (Feature 2) — the per-event service timetable surface.
// Captures the "Special Event Train Services" doc model: a header block
// (JOURNEY / RUN TIME / SET / CAPACITY) + a table of dated departures, plus
// Pullman scheduled-dining services with a block/parallel pill. Exports to a
// browser-print PDF (this event, or all events). Writes to dbo.Services and
// dbo.EventServiceInfo — the same tables the Schedule tab uses.

interface Props { eventId: number }

const TYPE_LABELS: Record<ServiceType, string> = {
  event: 'Event service', pullman: 'Pullman dining', dining: 'Dining', shuttle: 'Shuttle', other: 'Other'
}

export function ServiceTimetableTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: services, isLoading, error } = useServices(eventId)
  // Live MERAC sessions for this event (when bound). Shown as a read-only
  // reference so a manually-built (placeholder) timetable can be reconciled
  // against what's actually selling in MERAC — and pulled through when there's
  // no manual timetable at all.
  const { data: timetable } = useEventTimetable(eventId)
  const meracRows = timetable?.source === 'merac' ? (timetable.services ?? []) : []
  const [editing, setEditing] = useState<Service | 'new' | null>(null)

  const { event, pullman } = useMemo(() => {
    const list = services ?? []
    return {
      event: list.filter((s) => (s.serviceType ?? 'event') !== 'pullman' && (s.serviceType ?? 'event') !== 'dining'),
      pullman: list.filter((s) => s.serviceType === 'pullman' || s.serviceType === 'dining')
    }
  }, [services])

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading timetable…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load services</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Event Service Timetable</h2>
          <p className="text-sm text-ink-soft max-w-2xl">
            The timings and set plan for this event's train services. Mark Pullman
            dining services as running parallel to the event service or blocked out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/events/${eventId}/service-timetable`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5"
          >
            <Icon name="download" size={14} /> PDF — this event
          </a>
          <a
            href="/service-timetable-report"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5"
          >
            <Icon name="download" size={14} /> PDF — all events
          </a>
          <button
            type="button"
            disabled
            title="Coming soon — this will push the timetable below into MERAC. For now, build it in MERAC and it pulls through here to reconcile."
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft/50 inline-flex items-center gap-1.5 cursor-not-allowed"
          >
            <Icon name="train" size={14} /> Send to MERAC
          </button>
        </div>
      </div>

      <ServiceInfoCard eventId={eventId} archived={archived} />

      <MeracTimetablePanel rows={meracRows} meracCode={timetable?.meracEventId ?? null} />

      {!archived && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-3 py-1.5 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90"
          >
            + Add service
          </button>
        </div>
      )}

      <TimetableSection title="Event services" rows={event} onEdit={setEditing} eventId={eventId} showPill={false} archived={archived} />
      <TimetableSection title="Pullman / dining services" rows={pullman} onEdit={setEditing} eventId={eventId} showPill archived={archived} />

      {editing && (
        <ServiceTimetableEditor
          eventId={eventId}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Live MERAC reference panel --------------------------------------------
// Read-only pull-through of the event's MERAC sessions (when bound). Lets the
// manager reconcile a manually-built placeholder timetable against what's
// actually selling in MERAC; when there's no manual timetable this is the
// timetable the printed docs use too.

function fmtMeracDay(iso: string | null): string {
  if (!iso) return '—'
  const d = parseIsoDate(iso)
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

function MeracTimetablePanel({ rows, meracCode }: { rows: TimetableRow[]; meracCode: string | null }) {
  if (rows.length === 0) return null
  return (
    <div className="bg-white border border-green/40 rounded overflow-hidden">
      <div className="px-4 py-2.5 border-b border-line bg-green-soft/50 flex items-center gap-2 flex-wrap">
        <Icon name="train" size={15} className="text-green" />
        <h3 className="text-sm font-bold text-green uppercase tracking-wide">Live in MERAC</h3>
        {meracCode && <span className="text-xs text-ink-soft font-mono">{meracCode}</span>}
        <span className="text-xs text-ink-soft">pulled live — sessions, capacity &amp; bookings from the MERAC sync</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-soft">
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">Time</th>
              <th className="px-3 py-2 font-semibold">Session</th>
              <th className="px-3 py-2 font-semibold">From → To</th>
              <th className="px-3 py-2 font-semibold text-right">Capacity</th>
              <th className="px-3 py-2 font-semibold text-right">Booked</th>
              <th className="px-3 py-2 font-semibold text-right">Free</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => {
              const cap = r.capacity ?? 0
              const booked = r.meracBooked ?? 0
              const free = Math.max(0, cap - booked)
              return (
                <tr key={r.id} className="hover:bg-parchment/30">
                  <td className="px-3 py-2 whitespace-nowrap text-ink-soft">{fmtMeracDay(r.date)}</td>
                  <td className="px-3 py-2 font-mono">{r.timeText}</td>
                  <td className="px-3 py-2 font-semibold">{r.serviceLabel}</td>
                  <td className="px-3 py-2">{r.originStation || r.destinationStation ? `${r.originStation ?? '—'} → ${r.destinationStation ?? '—'}` : '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{cap || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{booked}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{cap ? free : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-xs text-ink-soft border-t border-line">
        These sessions come straight from MERAC. Build a placeholder timetable below to add loco / set-formation detail; matching it to these sessions is coming next.
      </div>
    </div>
  )
}

// ---- Header block ----------------------------------------------------------

function ServiceInfoCard({ eventId, archived }: { eventId: number; archived: boolean }) {
  const { data } = useServiceInfo(eventId)
  const updateMut = useUpdateServiceInfo(eventId)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<EventServiceInfo>({ journey: null, runTime: null, setFormation: null, capacityNote: null, notes: null })

  function open() {
    setForm(data ?? { journey: null, runTime: null, setFormation: null, capacityNote: null, notes: null })
    setEditing(true)
  }

  function save(e: FormEvent) {
    e.preventDefault()
    updateMut.mutate(form, { onSuccess: () => setEditing(false) })
  }

  const info = data ?? { journey: null, runTime: null, setFormation: null, capacityNote: null, notes: null }
  const hasAny = info.journey || info.runTime || info.setFormation || info.capacityNote || info.notes

  return (
    <div className="bg-white border border-line rounded p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2 flex-1">
          <HeaderField label="Journey" value={info.journey} />
          <HeaderField label="Run time" value={info.runTime} />
          <HeaderField label="Set / formation" value={info.setFormation} />
          <HeaderField label="Capacity" value={info.capacityNote} />
          {info.notes && <div className="sm:col-span-2"><HeaderField label="Notes" value={info.notes} /></div>}
          {!hasAny && <div className="text-sm text-ink-soft italic">No service header set yet — add the journey, run time, set and capacity.</div>}
        </div>
        {!archived && (
          <button
            type="button"
            onClick={open}
            className="px-2.5 py-1 text-xs font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <Icon name="edit" size={12} /> Edit
          </button>
        )}
      </div>

      {editing && (
        <ModalShell
          title="Service header"
          onClose={() => setEditing(false)}
          onSubmit={save}
          size="lg"
          footer={
            <>
              <CancelButton onClick={() => setEditing(false)} disabled={updateMut.isPending} />
              <PrimaryButton disabled={updateMut.isPending}>{updateMut.isPending ? 'Saving…' : 'Save'}</PrimaryButton>
            </>
          }
          body={
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Journey" hint="e.g. Pickering to Levisham">
                  <input className={inputClass(false)} value={form.journey ?? ''} onChange={(e) => setForm({ ...form, journey: e.target.value || null })} maxLength={160} />
                </Field>
                <Field label="Run time" hint="e.g. 1 hour">
                  <input className={inputClass(false)} value={form.runTime ?? ''} onChange={(e) => setForm({ ...form, runTime: e.target.value || null })} maxLength={60} />
                </Field>
                <Field label="Set / formation" hint="e.g. 64 x 3, 39 x 2">
                  <input className={inputClass(false)} value={form.setFormation ?? ''} onChange={(e) => setForm({ ...form, setFormation: e.target.value || null })} maxLength={160} />
                </Field>
                <Field label="Capacity" hint="e.g. 318 per trip">
                  <input className={inputClass(false)} value={form.capacityNote ?? ''} onChange={(e) => setForm({ ...form, capacityNote: e.target.value || null })} maxLength={160} />
                </Field>
              </div>
              <Field label="Notes (optional)">
                <textarea className={inputClass(false)} rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} />
              </Field>
              {updateMut.error && <ServerError title="Could not save" error={updateMut.error as Error} />}
            </div>
          }
        />
      )}
    </div>
  )
}

function HeaderField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-ink-soft">{label}</div>
      <div className="text-sm">{value ?? <span className="text-ink-soft">—</span>}</div>
    </div>
  )
}

// ---- Timetable section -----------------------------------------------------

function TimetableSection({
  title, rows, onEdit, eventId, showPill, archived
}: {
  title: string
  rows: Service[]
  onEdit: (s: Service) => void
  eventId: number
  showPill: boolean
  archived: boolean
}) {
  if (rows.length === 0 && !showPill) {
    return (
      <div className="bg-white border border-line rounded p-6 text-center text-sm text-ink-soft">
        No event services yet — add one with “+ Add service”.
      </div>
    )
  }
  if (rows.length === 0 && showPill) return null

  return (
    <div className="bg-white border border-line rounded overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-ink-soft font-mono">{rows.length}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-soft">
            <th className="px-3 py-2 font-semibold">Dep.</th>
            <th className="px-3 py-2 font-semibold">Arr.</th>
            <th className="px-3 py-2 font-semibold">Service</th>
            <th className="px-3 py-2 font-semibold">Loco</th>
            <th className="px-3 py-2 font-semibold">From → To</th>
            <th className="px-3 py-2 font-semibold">Set</th>
            <th className="px-3 py-2 font-semibold text-right">Cap.</th>
            {showPill && <th className="px-3 py-2 font-semibold">In document</th>}
            <th className="px-3 py-2 font-semibold w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((s) => (
            <ServiceRow key={s.id} s={s} onEdit={onEdit} eventId={eventId} showPill={showPill} archived={archived} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ServiceRow({ s, onEdit, eventId, showPill, archived }: { s: Service; onEdit: (s: Service) => void; eventId: number; showPill: boolean; archived: boolean }) {
  const updateMut = useUpdateService(eventId)

  function setPullmanMode(mode: PullmanMode) {
    updateMut.mutate({ id: s.id, body: { ...toBody(s), pullmanMode: mode } })
  }

  return (
    <tr className="hover:bg-parchment/30">
      <td className="px-3 py-2 font-mono">{s.timeText}</td>
      <td className="px-3 py-2 font-mono text-ink-soft">{s.arrivalTime ?? '—'}</td>
      <td className="px-3 py-2 font-semibold">{s.serviceLabel}</td>
      <td className="px-3 py-2">{s.loco ?? '—'}</td>
      <td className="px-3 py-2">{s.originStation || s.destinationStation ? `${s.originStation ?? '—'} → ${s.destinationStation ?? '—'}` : '—'}</td>
      <td className="px-3 py-2 text-ink-soft">{s.setFormation ?? '—'}</td>
      <td className="px-3 py-2 text-right font-mono">{s.capacity ?? '—'}</td>
      {showPill && (
        <td className="px-3 py-2">
          <div className="inline-flex rounded border border-line overflow-hidden text-xs font-semibold">
            {PULLMAN_MODES.map((m) => {
              const active = (s.pullmanMode ?? 'parallel') === m
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPullmanMode(m)}
                  disabled={updateMut.isPending || archived}
                  className="px-2.5 py-1 transition-colors capitalize disabled:opacity-70"
                  style={active
                    ? { background: m === 'block' ? '#f8dbdd' : '#dcebe5', color: m === 'block' ? '#7a2329' : '#0a4a3a' }
                    : { background: 'transparent', color: '#57534e' }}
                  title={m === 'block' ? 'Blocked out — omitted from the printed timetable' : 'Runs parallel — shown alongside the event service'}
                >
                  {m === 'block' ? 'Blocked' : 'Parallel'}
                </button>
              )
            })}
          </div>
        </td>
      )}
      <td className="px-3 py-2 text-right">
        {!archived && (
          <button type="button" onClick={() => onEdit(s)} aria-label={`Edit ${s.serviceLabel}`} className="text-ink-soft hover:text-green">
            <Icon name="edit" size={14} />
          </button>
        )}
      </td>
    </tr>
  )
}

// Build a full ServiceCreate/Update body from a Service (full-replacement PUT).
function toBody(s: Service): ServiceCreate {
  return {
    serviceLabel: s.serviceLabel,
    timeText: s.timeText,
    loco: s.loco,
    originStation: s.originStation,
    destinationStation: s.destinationStation,
    capacity: s.capacity,
    notes: s.notes,
    sortOrder: s.sortOrder,
    dateOverride: s.dateOverride ?? null,
    booked: s.booked ?? 0,
    serviceType: s.serviceType ?? 'event',
    pullmanMode: s.pullmanMode ?? null,
    arrivalTime: s.arrivalTime ?? null,
    setFormation: s.setFormation ?? null
  }
}

// ---- Service editor --------------------------------------------------------

function ServiceTimetableEditor({ eventId, initial, onClose }: { eventId: number; initial: Service | null; onClose: () => void }) {
  const isEditing = initial != null
  const createMut = useCreateService(eventId)
  const updateMut = useUpdateService(eventId)
  const deleteMut = useDeleteService(eventId)

  const [serviceLabel, setLabel] = useState(initial?.serviceLabel ?? '')
  const [serviceType, setType] = useState<ServiceType>(initial?.serviceType ?? 'event')
  const [timeText, setTime] = useState(initial?.timeText ?? '')
  const [arrivalTime, setArrival] = useState(initial?.arrivalTime ?? '')
  const [loco, setLoco] = useState(initial?.loco ?? '')
  const [originStation, setOrigin] = useState(initial?.originStation ?? '')
  const [destinationStation, setDest] = useState(initial?.destinationStation ?? '')
  const [setFormation, setSet] = useState(initial?.setFormation ?? '')
  const [capacity, setCapacity] = useState(initial?.capacity == null ? '' : String(initial.capacity))
  const [pullmanMode, setPullman] = useState<PullmanMode>(initial?.pullmanMode ?? 'parallel')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/
  const errors: { label?: string; time?: string; arrival?: string; capacity?: string } = {}
  if (serviceLabel.trim().length === 0) errors.label = 'Required'
  if (!TIME_RE.test(timeText.trim())) errors.time = 'HH:MM'
  if (arrivalTime.trim() !== '' && !TIME_RE.test(arrivalTime.trim())) errors.arrival = 'HH:MM'
  if (capacity.trim() !== '' && (!Number.isInteger(Number(capacity)) || Number(capacity) < 0)) errors.capacity = 'Whole number'
  const isValid = Object.keys(errors).length === 0
  const isPullman = serviceType === 'pullman' || serviceType === 'dining'
  const saving = createMut.isPending || updateMut.isPending
  const serverError = (createMut.error ?? updateMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: ServiceCreate = {
      serviceLabel: serviceLabel.trim(),
      timeText: timeText.trim(),
      loco: loco.trim() || null,
      originStation: originStation.trim() || null,
      destinationStation: destinationStation.trim() || null,
      capacity: capacity.trim() === '' ? null : Number(capacity),
      notes: notes.trim() || null,
      sortOrder: initial?.sortOrder ?? 0,
      dateOverride: initial?.dateOverride ?? null,
      booked: initial?.booked ?? 0,
      serviceType,
      pullmanMode: isPullman ? pullmanMode : null,
      arrivalTime: arrivalTime.trim() || null,
      setFormation: setFormation.trim() || null
    }
    if (isEditing && initial) updateMut.mutate({ id: initial.id, body }, { onSuccess: () => onClose() })
    else createMut.mutate(body, { onSuccess: () => onClose() })
  }

  function handleDelete() {
    if (!initial) return
    if (window.confirm(`Remove service "${initial.serviceLabel}"?`)) deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit service' : 'New service'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="xl"
      footer={
        <>
          {isEditing && <DangerButton onClick={handleDelete} disabled={saving || deleteMut.isPending}>{deleteMut.isPending ? 'Removing…' : 'Remove'}</DangerButton>}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add service'}</PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service label" error={errors.label}>
              <input className={inputClass(!!errors.label)} value={serviceLabel} onChange={(e) => setLabel(e.target.value)} maxLength={60} autoFocus placeholder="e.g. Trip 1, 1T01" />
            </Field>
            <Field label="Type">
              <select className={inputClass(false)} value={serviceType} onChange={(e) => setType(e.target.value as ServiceType)}>
                {SERVICE_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="Departure" error={errors.time} hint="HH:MM">
              <input className={inputClass(!!errors.time)} value={timeText} onChange={(e) => setTime(e.target.value)} placeholder="17:00" />
            </Field>
            <Field label="Arrival / return" error={errors.arrival} hint="HH:MM (optional)">
              <input className={inputClass(!!errors.arrival)} value={arrivalTime} onChange={(e) => setArrival(e.target.value)} placeholder="18:00" />
            </Field>
            <Field label="Loco (optional)">
              <input className={inputClass(false)} value={loco} onChange={(e) => setLoco(e.target.value)} maxLength={120} />
            </Field>
            <Field label="Set / formation (optional)">
              <input className={inputClass(false)} value={setFormation} onChange={(e) => setSet(e.target.value)} maxLength={120} placeholder="64 x 3, 39 x 2" />
            </Field>
            <Field label="From (optional)">
              <input className={inputClass(false)} value={originStation} onChange={(e) => setOrigin(e.target.value)} maxLength={80} placeholder="Pickering" />
            </Field>
            <Field label="To (optional)">
              <input className={inputClass(false)} value={destinationStation} onChange={(e) => setDest(e.target.value)} maxLength={80} placeholder="Levisham" />
            </Field>
            <Field label="Capacity (optional)" error={errors.capacity}>
              <input className={inputClass(!!errors.capacity)} inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="318" />
            </Field>
            {isPullman && (
              <Field label="In document" hint="Pullman/dining only">
                <select className={inputClass(false)} value={pullmanMode} onChange={(e) => setPullman(e.target.value as PullmanMode)}>
                  {PULLMAN_MODES.map((m) => <option key={m} value={m}>{m === 'block' ? 'Blocked out' : 'Runs parallel'}</option>)}
                </select>
              </Field>
            )}
          </div>
          <Field label="Notes (optional)">
            <textarea className={inputClass(false)} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}
